import { QueryClient } from "@tanstack/react-query";

import { ApiError } from "~/lib/api";

/**
 * The shared query client.
 *
 * Retry policy is the interesting part: a 4xx means the request itself was wrong
 * (bad id, expired session) and retrying just multiplies the failure, so only
 * 5xx and transport errors are retried, with backoff.
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Editors read their own writes: mutations invalidate these keys, so a short
      // freshness window only has to cover navigating back and forth.
      staleTime: 30_000,
      gcTime: 30 * 60_000,
      retry: (failureCount, error) => {
        if (error instanceof ApiError && error.status < 500) return false;
        return failureCount < 3;
      },
      retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 15_000),
      refetchOnWindowFocus: false,
    },
  },
});

/** Query key factory, so no key string is written twice. */
interface AnalyticsKeyParams {
  from: string;
  to: string;
  locale: string | undefined;
}

/** The window and locale every analytics key starts with, normalised once. */
function analyticsScope(params: AnalyticsKeyParams) {
  return [params.from, params.to, params.locale ?? "all"] as const;
}

export const queryKeys = {
  authConfig: ["auth", "config"] as const,
  me: ["auth", "me"] as const,
  articles: (params: {
    locale: string | undefined;
    search: string | undefined;
    sortBy: string;
    sortOrder: string;
  }) =>
    [
      "admin",
      "news",
      "list",
      params.locale ?? "all",
      params.search ?? "",
      params.sortBy,
      params.sortOrder,
    ] as const,
  /**
   * Root of every list key, for invalidating all locale filters at once. The "list"
   * segment keeps it from also matching the detail keys — invalidating the lists
   * after a delete must not refetch the just-deleted article's detail.
   */
  articlesRoot: ["admin", "news", "list"] as const,
  article: (id: string) => ["admin", "news", "detail", id] as const,
  covers: (params: { topic: string | undefined; search: string | undefined }) =>
    [
      "admin",
      "covers",
      "list",
      params.topic ?? "all",
      params.search ?? "",
    ] as const,
  /** Every library page: a new, removed or newly used cover changes them all. */
  coversRoot: ["admin", "covers"] as const,
  events: (params: {
    locale: string | undefined;
    search: string | undefined;
    isPublished: boolean | undefined;
    sortBy: string;
    sortOrder: string;
  }) =>
    [
      "admin",
      "events",
      "list",
      params.locale ?? "all",
      params.search ?? "",
      params.isPublished ?? "all",
      params.sortBy,
      params.sortOrder,
    ] as const,
  eventsRoot: ["admin", "events", "list"] as const,
  event: (id: string) => ["admin", "events", "detail", id] as const,
  eventRegistrations: (id: string) =>
    ["admin", "events", "registrations", id] as const,
  eventQuestions: (
    id: string,
    params: { unansweredOnly: boolean; includeHidden: boolean },
  ) =>
    [
      "admin",
      "events",
      "questions",
      id,
      params.unansweredOnly,
      params.includeHidden,
    ] as const,
  eventQuestionsRoot: (id: string) =>
    ["admin", "events", "questions", id] as const,
  /** Webinar recordings an event can point at, per language. */
  recordingOptions: (locale: string) =>
    ["admin", "news", "recordings", locale] as const,
  dashboardHighlights: (params: {
    locale: string | undefined;
    search: string | undefined;
    isVisible: boolean | undefined;
    sortBy: string;
    sortOrder: string;
  }) =>
    [
      "admin",
      "dashboard-highlights",
      "list",
      params.locale ?? "all",
      params.search ?? "",
      params.isVisible ?? "all",
      params.sortBy,
      params.sortOrder,
    ] as const,
  dashboardHighlightsRoot: ["admin", "dashboard-highlights"] as const,
  dashboardHighlightListsRoot: [
    "admin",
    "dashboard-highlights",
    "list",
  ] as const,
  dashboardHighlight: (id: string) =>
    ["admin", "dashboard-highlights", "detail", id] as const,
  users: (params: {
    search: string | undefined;
    isActive: boolean | undefined;
    hasMembershipOverride: boolean | undefined;
  }) =>
    [
      "admin",
      "users",
      "list",
      params.search ?? "",
      params.isActive ?? "all",
      params.hasMembershipOverride ?? "all",
    ] as const,
  usersRoot: ["admin", "users", "list"] as const,
  user: (id: string) => ["admin", "users", "detail", id] as const,
  membershipInvites: ["admin", "membership-invites"] as const,
  chatThreads: (params: { search: string | undefined; unreadOnly: boolean }) =>
    [
      "admin",
      "chat",
      "threads",
      params.search ?? "",
      params.unreadOnly,
    ] as const,
  chatThreadsRoot: ["admin", "chat", "threads"] as const,
  chatThread: (id: string) => ["admin", "chat", "thread", id] as const,
  analyticsOverview: (params: AnalyticsKeyParams) =>
    ["admin", "analytics", "overview", ...analyticsScope(params)] as const,
  analyticsTimeseries: (params: AnalyticsKeyParams) =>
    ["admin", "analytics", "timeseries", ...analyticsScope(params)] as const,
  analyticsContent: (
    params: AnalyticsKeyParams & {
      subjectType: string;
      format: string | undefined;
      topic: string | undefined;
    },
  ) =>
    [
      "admin",
      "analytics",
      "content",
      "list",
      ...analyticsScope(params),
      params.subjectType,
      params.format ?? "all",
      params.topic ?? "all",
    ] as const,
  analyticsContentRoot: ["admin", "analytics", "content", "list"] as const,
  analyticsTaxonomy: (params: AnalyticsKeyParams) =>
    ["admin", "analytics", "taxonomy", ...analyticsScope(params)] as const,
  analyticsEvents: (params: AnalyticsKeyParams) =>
    ["admin", "analytics", "events", ...analyticsScope(params)] as const,
  analyticsPromotions: (params: AnalyticsKeyParams & { slot: string }) =>
    [
      "admin",
      "analytics",
      "promotions",
      ...analyticsScope(params),
      params.slot,
    ] as const,
  analyticsRetention: (params: AnalyticsKeyParams) =>
    ["admin", "analytics", "retention", params.from, params.to] as const,
};
